import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Box,
  Button,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, SyntheticEvent, useEffect, useState } from "react";
import computeMetrics from "../../utils/computeMetrics";
import { useExplorerData } from "../../hooks/useExplorerData";
import Metrics from "../Metrics";
import getMetricsForDisplay from "../../utils/getMetricsForDisplay";
import type { MetricsComparison } from "../Metrics";
import { useFilterData } from "../../hooks/useFilterData";

const InspectIndividualDatasets = () => {
  const [datasetOptions, setDatasetOptions] = useState<string[] | null>(null);
  const [stats, setStats] = useState<MetricsComparison | null>(null);
  const [selectedDataset, setSelectedDataset] = useState<{
    [key: string]: any;
  } | null>(null);

  const explorerData = useExplorerData();
  const { metaData } = explorerData;

  const { data, setData } = useFilterData();

  useEffect(() => {
    if (explorerData && explorerData.data) {
      const datasets = explorerData.data as any[];

      const filteredDatasets = data?.selectedModality?.toLowerCase() === "all" ? datasets : datasets.filter(
        (dataset) =>
          dataset["Modality"]?.toLowerCase() === data.selectedModality.toLowerCase()
      );

      const datasetCollection = filteredDatasets.map(
        (dataset) => dataset["Unique Dataset Identifier"]
      );

      setDatasetOptions(datasetCollection);

      // Optional: set the selected dataset based on the filtered list
      if (filteredDatasets.length > 0) {
        setSelectedDataset(filteredDatasets[0]);
      } else {
        setSelectedDataset(null); // or handle it gracefully
      }
    }
  }, [explorerData, data.selectedModality]);


  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (datasetOptions && selectedDataset) {
      const dataset = (explorerData.data as any).filter(
        (data: any) =>
          data["Unique Dataset Identifier"] ===
          selectedDataset["Unique Dataset Identifier"],
      );
      const metrics = computeMetrics(dataset, explorerData.constants);

      if (metrics && metaData) {
        setStats(getMetricsForDisplay(metrics, metaData));
      }
    }
  };

  const handleOptionSelect = (
    event: SyntheticEvent<Element, Event>,
    newValue: string | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<string> | undefined,
  ): void => {
    if (newValue) {
      if (explorerData) {
        const newSelection = (explorerData.data as any).filter(
          (data: any) => data["Unique Dataset Identifier"] === newValue,
        );
        setSelectedDataset(newSelection[0]);
      }
    }
  };

  return (
    <>
      <Box>
        <Box
          style={{
            border: "2px solid #aaa",
            borderRadius: "5px",
            padding: "20px",
          }}
        >
          <Typography>
            Select the dataset in this collection to inspect
          </Typography>
          <form onSubmit={handleSubmit}>
            {datasetOptions && selectedDataset && (
              <Autocomplete
                sx={{ maxWidth: "100%", width: 300, marginY: "20px" }}
                options={datasetOptions}
                autoHighlight
                value={selectedDataset["Unique Dataset Identifier"]}
                onChange={handleOptionSelect}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Choose an option"
                    inputProps={{
                      ...params.inputProps,
                    }}
                  />
                )}
              />
            )}
            <Button
              variant="outlined"
              sx={{
                color: "#aaa",
                border: "#aaa solid 1px",
                borderRadius: "5px",
              }}
              type="submit"
            >
              Submit Selection
            </Button>
          </form>
        </Box>

        {stats && (
          <Box sx={{ my: 3 }}>
            <Metrics metrics={stats} />
          </Box>
        )}
        {selectedDataset && stats && (
          <Box
            sx={{
              display: "grid",
              gap: "16px",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gap: "10px",
                "*": {
                  "word-break": "break-word !important",
                },
              }}
            >
              <Typography
                variant="h5"
                component="h4"
                sx={{
                  fontWeight: "bold",
                }}
              >
                Dataset Information
              </Typography>

              <Typography>
                Unique Dataset Identifier: &nbsp;
                {selectedDataset["Unique Dataset Identifier"]}
              </Typography>
              <Typography>
                Paper Title: &nbsp;
                {selectedDataset["Paper Title"] || (
                  <small> Not available</small>
                )}
              </Typography>
              <Typography>
                Dataset URL:{" "}
                <Link
                  sx={{ display: "inline" }}
                  href={selectedDataset["Dataset URL"]}
                >
                  {" "}
                  {selectedDataset["Dataset URL"]}
                </Link>
              </Typography>
              <Typography>
                Hugging Face URL:{" "}
                <Link
                  sx={{ display: "inline" }}
                  href={selectedDataset["Hugging Face URL"]}
                >
                  {selectedDataset["Hugging Face URL"]}
                </Link>
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gap: "10px",
              }}
            >
              <Typography
                variant="h5"
                component="h4"
                sx={{
                  fontWeight: "bold",
                }}
              >
                Dataset Characteristics
              </Typography>
              <Typography sx={{ fontWeight: "bold" }}>Format:</Typography>
              <ul>
                {selectedDataset.Format.map((data: string[], index: number) => (
                  // todo: more robust key
                  <li key={index}>{data}</li>
                ))}
              </ul>
              <Typography sx={{ fontWeight: "bold" }}>Languages:</Typography>
              <ul>
                {selectedDataset.Languages.map(
                  (data: string, index: number) => (
                    <li key={index}>{data}</li>
                  ),
                )}
              </ul>
              <Typography fontWeight={"bold"}>Task Categories:</Typography>
              <ul>
                {(selectedDataset?.["Task Categories"] ?? [])
                  .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index)
                  .map((data: string, index: number) => (
                    <li key={index}>{data}</li>
                  ))}
              </ul>

              <Typography fontWeight={"bold"}>Text Topics:</Typography>
              <ul>
                {selectedDataset?.["Inferred Metadata"]?.["Text Topics"]?.length > 0 ? (
                  selectedDataset["Inferred Metadata"]["Text Topics"].map(
                    (data: string, index: number) => <li key={index}>{data}</li>,
                  )
                ) : (
                  <li>No topics available</li>
                )}
              </ul>
            </Box>

            <Box
              sx={{
                display: "grid",
                gap: "10px",
              }}
            >
              <Typography
                variant="h5"
                component="h4"
                sx={{
                  fontWeight: "bold",
                }}
              >
                Data Statistics
              </Typography>
              {
                selectedDataset["Modality"].toLowerCase() === "text" &&
                <>

                  <Typography>
                    Num Exs:{" "}
                    {selectedDataset?.["Text Metrics"]?.["Num Dialogs"]?.toFixed
                      ? selectedDataset["Text Metrics"]["Num Dialogs"].toFixed(1)
                      : "N/A"}
                  </Typography>
                  <Typography>
                    Input Length (characters) [Minimum | Mean | Maximum]:{" "}
                    {typeof selectedDataset?.["Text Metrics"]?.["Min Inputs Length"] === "number"
                      ? selectedDataset["Text Metrics"]["Min Inputs Length"].toFixed(1)
                      : "N/A"}{" "}
                    |{" "}
                    {typeof selectedDataset?.["Text Metrics"]?.["Mean Inputs Length"] === "number"
                      ? selectedDataset["Text Metrics"]["Mean Inputs Length"].toFixed(1)
                      : "N/A"}{" "}
                    |{" "}
                    {typeof selectedDataset?.["Text Metrics"]?.["Max Inputs Length"] === "number"
                      ? selectedDataset["Text Metrics"]["Max Inputs Length"].toFixed(1)
                      : "N/A"}
                  </Typography>


                  <Typography>
                    Target Length (characters) [Minimum | Mean | Maximum]:{" "}
                    {typeof selectedDataset?.["Text Metrics"]?.["Min Targets Length"] === "number"
                      ? selectedDataset["Text Metrics"]["Min Targets Length"].toFixed(1)
                      : "N/A"}{" "}
                    |{" "}
                    {typeof selectedDataset?.["Text Metrics"]?.["Mean Targets Length"] === "number"
                      ? selectedDataset["Text Metrics"]["Mean Targets Length"].toFixed(1)
                      : "N/A"}{" "}
                    |{" "}
                    {typeof selectedDataset?.["Text Metrics"]?.["Max Targets Length"] === "number"
                      ? selectedDataset["Text Metrics"]["Max Targets Length"].toFixed(1)
                      : "N/A"}
                  </Typography>
                </>
              }
              {
                (selectedDataset["Modality"].toLowerCase() === "speech" || selectedDataset["Modality"].toLowerCase() === "video") &&
                <Typography>
                  Hours:{" "}
                  {selectedDataset?.["Hours"]
                    ? selectedDataset?.["Hours"]
                    : "N/A"}{" "}

                </Typography>
              }


            </Box>
            <Box
              sx={{
                display: "grid",
                gap: "10px",
              }}
            >
              <Typography
                variant="h5"
                component="h4"
                sx={{
                  fontWeight: "bold",
                }}
              >
                Data Provenance
              </Typography>
              <Typography fontWeight={"bold"}>Creators:</Typography>
              <ul>
                {selectedDataset.Creators?.length ? (
                  (selectedDataset.Creators as any).map(
                    (data: any, index: number) => <li key={index}>{data}</li>,
                  )
                ) : (
                  <small>No data available</small>
                )}
              </ul>
              <Typography fontWeight={"bold"}>Text Sources:</Typography>
              <ul>
                {selectedDataset["Text Sources"]?.length ? (
                  (selectedDataset["Text Sources"] as any).map(
                    (data: any, index: number) => <li key={index}>{data}</li>,
                  )
                ) : (
                  <small>No data available</small>
                )}
              </ul>
              <Typography fontWeight={"bold"}>Licenses:</Typography>
              <ul>
                {selectedDataset.Licenses?.length ? (
                  (selectedDataset.Licenses as any).map(
                    (data: any, index: number) => (
                      <li key={index}>{data.License}</li>
                    ),
                  )
                ) : (
                  <small>No data available</small>
                )}
              </ul>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default InspectIndividualDatasets;
